import {
    BottomSheetBackdrop,
    type BottomSheetBackdropProps,
    BottomSheetModal,
    type BottomSheetModalProps,
    BottomSheetView,
    useBottomSheetTimingConfigs,
} from '@gorhom/bottom-sheet';
import * as React from 'react';
import {
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    Keyboard,
    type KeyboardEvent as RNKeyboardEvent,
    type KeyboardEventEasing,
    type LayoutChangeEvent,
    type NativeScrollEvent,
    type NativeSyntheticEvent,
    Platform,
    type ScrollView as RNScrollView,
    useWindowDimensions,
} from 'react-native';
import {
    Easing,
    ReduceMotion,
    useReducedMotion,
    type WithSpringConfig,
    type WithTimingConfig,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { FullWindowOverlay } from 'react-native-screens';
import { isWeb, ScrollView, View, YStack } from 'tamagui';

import { isNonMobileWeb } from '../../constants';
import { SimpleModal } from '../modals/Modals';
import {
    getMaxSheetHeight,
    getPaddingForScrollGradient,
    SheetContent,
    SheetScrollGradient,
    WEB_SHEET_MIN_HEIGHT,
} from './SheetContent';
import { getSheetHeaderHeight, SheetHeader } from './SheetHeader';

const ROUNDED_CORNER_RADIUS = 30;

// Mirrors @gorhom/bottom-sheet's own (unexported) getKeyboardAnimationConfigs,
// so our keyboardWillHide fallback below can animate using the same
// duration/easing the OS reports for its own keyboard dismiss, instead of an
// unrelated fixed config that would look like a distinct, mistimed motion.
const getKeyboardAnimationConfig = (
    easing: KeyboardEventEasing,
    duration: number,
): WithSpringConfig | WithTimingConfig => {
    switch (easing) {
        case 'easeIn':
            return { easing: Easing.in(Easing.ease), duration };
        case 'easeOut':
            return { easing: Easing.out(Easing.ease), duration };
        case 'easeInEaseOut':
            return { easing: Easing.inOut(Easing.ease), duration };
        case 'linear':
            return { easing: Easing.linear, duration };
        case 'keyboard':
            return {
                damping: 500,
                stiffness: 1000,
                mass: 3,
                overshootClamping: true,
                restDisplacementThreshold: 10,
                restSpeedThreshold: 10,
            };
    }
};

interface KeyboardCompatibleBottomSheetProps extends BottomSheetModalProps {
    onDismiss: () => void;
    onPressBackdrop?: () => void;
    dismissOnBackdropPress?: boolean;
    disableDrag?: boolean;
    isInsideModal?: boolean;
    children: ReactNode;
    sheetRef?: React.RefObject<BottomSheetModal | null>;
    title?: string;
    subtitle?: string;
    titleLineHeight?: number;
    showCloseButton: boolean;
    headerLeftAction?: ReactNode;
    withPadding?: boolean;
    fixedContentHeight?: number;
    forceMaxContentHeight?: boolean;
    // Opts a dynamically-sized sheet into the same ScrollView keyboard-inset
    // handling forceMaxContentHeight sheets get (see
    // automaticallyAdjustKeyboardInsets below), without forcing max height.
    // For content tall enough that it might need to scroll past the keyboard
    // even after the sheet grows as far as it can — as a fallback in case
    // that growth doesn't happen in time (e.g. a fast first interaction
    // racing gorhom's own async content-size measurement).
    keyboardScrollFallback?: boolean;
    contentScrollRef?: React.RefObject<RNScrollView | null>;
    onContentScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
    scrollContainerOverride?: (children: ReactNode) => ReactNode;
}

/** TODO This should not be exported as a separate component from Sheet, but instead as one variant of the Sheet component based on a specific prop or determined by conditions of other props */
export const KeyboardCompatibleBottomSheet = ({
    onDismiss,
    onPressBackdrop,
    dismissOnBackdropPress = true,
    disableDrag = false,
    isInsideModal,
    children,
    sheetRef,
    title,
    subtitle,
    titleLineHeight,
    showCloseButton,
    headerLeftAction,
    withPadding = true,
    fixedContentHeight,
    forceMaxContentHeight,
    keyboardScrollFallback,
    contentScrollRef,
    onContentScroll,
    scrollContainerOverride,
    ...props
}: KeyboardCompatibleBottomSheetProps) => {
    const reducedMotion = useReducedMotion();
    const { top, bottom } = useSafeAreaInsets();
    const { height: liveScreenHeight } = useWindowDimensions();
    // On mobile web (Safari), opening the on-screen keyboard shrinks the
    // visual viewport, which causes useWindowDimensions to report a smaller
    // height. Lock to the initial value so the sheet doesn't shrink when an
    // input is focused.
    const initialScreenHeightRef = useRef(liveScreenHeight);
    const screenHeight = isWeb
        ? initialScreenHeightRef.current
        : liveScreenHeight;
    const [shouldShowScrollGradient, setShouldShowScrollGradient] =
        useState(false);

    const paddingForScrollGradient = useMemo(() => {
        return getPaddingForScrollGradient(shouldShowScrollGradient, bottom);
    }, [shouldShowScrollGradient, bottom]);
    const maxSheetHeight = useMemo(() => {
        return getMaxSheetHeight(screenHeight, top);
    }, [screenHeight, top]);
    const sheetContentMaxHeight = useMemo(() => {
        return (
            maxSheetHeight -
            getSheetHeaderHeight({
                title,
                showCloseButton,
                leftAction: headerLeftAction,
                withPadding,
            })
        );
    }, [title, showCloseButton, headerLeftAction, withPadding, maxSheetHeight]);
    const contentHeight = useMemo(() => {
        return forceMaxContentHeight
            ? sheetContentMaxHeight
            : fixedContentHeight;
    }, [forceMaxContentHeight, fixedContentHeight, sheetContentMaxHeight]);

    const internalRef = useRef<BottomSheetModal>(null);
    const ref = sheetRef ?? internalRef;

    // A backdrop must be provided if we want to have one.
    // The library provides a default backdrop, but 'click outside to dismiss' only seems to work on native.
    // For Web, we provide a custom backdrop so we can manually implement this behavior.
    const renderBackdrop = useCallback(
        isWeb
            ? () => (
                  <View
                      position="absolute"
                      height="100%"
                      width="100%"
                      backgroundColor="rgba(0, 0, 0, 0.4)"
                      onPress={() => {
                          onPressBackdrop?.();
                          dismissOnBackdropPress && ref.current?.dismiss();
                      }}
                  />
              )
            : (backdropProps: BottomSheetBackdropProps) => (
                  <BottomSheetBackdrop
                      {...backdropProps}
                      style={[backdropProps.style]}
                      disappearsOnIndex={-1}
                      appearsOnIndex={0}
                      opacity={0.5}
                      enableTouchThrough={true}
                      onPress={() => {
                          onPressBackdrop?.();
                      }}
                      pressBehavior={dismissOnBackdropPress ? 'close' : 'none'}
                  />
              ),
        [],
    );

    const animationConfigs = useBottomSheetTimingConfigs({
        duration: 600,
        easing: Easing.bezier(0.65, 0, 0.35, 1),
    });

    useEffect(() => {
        ref.current?.present();
    }, []);

    // keyboardBlurBehavior="restore" (below) asks gorhom to resize the sheet
    // back down once the keyboard hides, but that reaction fires exactly once,
    // at the moment gorhom's own keyboard-status listener sees the change —
    // and it silently no-ops if content dynamic-sizing hasn't finished
    // measuring yet (gorhom's isLayoutCalculated gate, checked in both its
    // internal evaluatePosition and the public snapToIndex). The more content
    // a sheet has, the longer that measurement takes, so a fast first
    // focus-type-dismiss can beat it and leave the sheet stuck at keyboard
    // height.
    //
    // This listens on 'keyboardWillHide' (iOS only — Android doesn't fire
    // "will" keyboard events reliably) rather than 'keyboardDidHide', which
    // only fires once the OS's own dismiss animation has already finished —
    // reacting to it necessarily runs our correction *after* the keyboard has
    // fully disappeared instead of alongside it, which is what made an
    // earlier version of this fix (using keyboardDidHide, tuned to various
    // delays and animation durations) look sluggish no matter how it was
    // tuned. 'keyboardWillHide' fires as the OS dismiss animation begins, and
    // its event carries that animation's own duration/easing — mirrored here
    // via getKeyboardAnimationConfig — so our fallback runs in parallel with,
    // and visually matches, the keyboard's own motion instead of a distinct
    // animation tacked on afterward.
    //
    // snapToIndex(0) is a safe no-op once the sheet is already at its resting
    // position, so firing this on every keyboardWillHide — even when gorhom's
    // own reaction already succeeded — is a harmless safety net for the case
    // where it didn't.
    //
    // keyboardWillHide fires early enough that isLayoutCalculated could still
    // be false in rare cases (e.g. an unusually fast first interaction right
    // after the sheet opens), in which case this call would also no-op. As a
    // last-resort backstop for exactly that case, also retry on keyboardDidHide
    // — by then layout measurement has certainly finished. In the common case
    // where keyboardWillHide already succeeded, this is just another no-op.
    useEffect(() => {
        const willHideSubscription = Keyboard.addListener(
            'keyboardWillHide',
            (event: RNKeyboardEvent) => {
                ref.current?.snapToIndex(
                    0,
                    getKeyboardAnimationConfig(event.easing, event.duration),
                );
            },
        );
        const didHideSubscription = Keyboard.addListener(
            'keyboardDidHide',
            () => {
                ref.current?.snapToIndex(0);
            },
        );
        return () => {
            willHideSubscription.remove();
            didHideSubscription.remove();
        };
    }, [ref]);

    // this is required to be able to display sheets on top of iOS modals
    // https://github.com/gorhom/react-native-bottom-sheet/issues/1644#issuecomment-1949019839
    const containerComponent = useCallback((containerProps: unknown) => {
        if (isWeb) {
            return undefined;
        }

        // @ts-expect-error unsure what the type of props should be here?
        return <FullWindowOverlay>{containerProps.children}</FullWindowOverlay>;
    }, []);

    const onContentLayout = useCallback(
        (event: LayoutChangeEvent) => {
            const { height } = event.nativeEvent.layout;
            if (height != null && height > sheetContentMaxHeight) {
                setShouldShowScrollGradient(true);
            }
        },
        [sheetContentMaxHeight],
    );

    const buildSheetContent = (height?: number) => {
        return (
            <>
                <SheetHeader
                    withPadding={withPadding}
                    title={title}
                    subtitle={subtitle}
                    showCloseButton={showCloseButton}
                    onClose={onDismiss}
                    leftAction={headerLeftAction}
                    titleLineHeight={titleLineHeight}
                />
                {scrollContainerOverride != null ? (
                    scrollContainerOverride(
                        <SheetContent
                            withPadding={withPadding}
                            height={height ?? '100%'}
                            maxHeight={sheetContentMaxHeight}
                            shouldShowScrollGradient={false}
                        >
                            {children}
                        </SheetContent>,
                    )
                ) : (
                    <ScrollView
                        ref={
                            contentScrollRef as React.Ref<
                                React.ComponentRef<typeof ScrollView>
                            >
                        }
                        maxHeight={sheetContentMaxHeight}
                        onScroll={onContentScroll}
                        scrollEventThrottle={
                            typeof onContentScroll === 'function'
                                ? 16
                                : undefined
                        }
                        showsVerticalScrollIndicator={isWeb}
                        keyboardShouldPersistTaps="handled"
                        // forceMaxContentHeight sheets need this: their frame
                        // can't grow (it's already at its max), so the
                        // ScrollView needs its own room to scroll content past
                        // the keyboard. keyboardScrollFallback opts a
                        // dynamically-sized sheet into the same thing, as a
                        // fallback for content tall enough that gorhom's own
                        // 'interactive' growth might not be enough (or might
                        // race gorhom's async content measurement on a fast
                        // first interaction). Left off by default, since
                        // automaticallyAdjustKeyboardInsets applies iOS's own
                        // "keep the focused field visible" auto-scroll
                        // regardless of whether it's actually needed, which
                        // pushed title/Delete off-screen unnecessarily on
                        // short sheets that already fit fine.
                        automaticallyAdjustKeyboardInsets={
                            forceMaxContentHeight ?? keyboardScrollFallback
                        }
                        contentContainerStyle={{
                            paddingBottom: paddingForScrollGradient,
                        }}
                    >
                        <SheetContent
                            withPadding={withPadding}
                            // minHeight (not height) so short content still fills
                            // the sheet, but taller content — or the same content
                            // once the keyboard shrinks the visible area — can grow
                            // past that and actually become scrollable. A fixed
                            // height here clamps content to the viewport, leaving
                            // nothing for the ScrollView to scroll to and cutting
                            // off anything (e.g. Save/Cancel) below the fold.
                            minHeight={height ?? '100%'}
                            shouldShowScrollGradient={shouldShowScrollGradient}
                            onLayout={onContentLayout}
                        >
                            {children}
                        </SheetContent>
                    </ScrollView>
                )}
                {shouldShowScrollGradient && <SheetScrollGradient />}
            </>
        );
    };

    if (isNonMobileWeb) {
        return (
            <SimpleModal
                isOpen={true}
                onClose={onDismiss}
                dismissOnOverlayPress={dismissOnBackdropPress}
                withPadding={false}
                dialogProps={{
                    overflow: 'hidden',
                    minHeight: WEB_SHEET_MIN_HEIGHT,
                    maxHeight: maxSheetHeight + 2, // + 2 adjustment prevents unnecessary scrollbars
                    onOpenAutoFocus: (e: Event) => {
                        e.preventDefault();
                    },
                }}
            >
                {buildSheetContent(contentHeight)}
            </SimpleModal>
        );
    }

    return (
        <BottomSheetModal
            index={0}
            ref={ref}
            onDismiss={onDismiss}
            enableDynamicSizing={true}
            detached={isWeb} // if false on mobile web, sheet content is sometimes off the screen
            enableOverDrag={!isWeb}
            // Previously hardcoded true regardless of disableDrag, leaving the
            // sheet's own content-area pan gesture recognizer active even when
            // callers asked to disable dragging — which can compete with a
            // nested (non-gorhom) ScrollView's own scroll gesture and block
            // scrolling entirely once content is tall enough to need it.
            // disableDrag should mean "no sheet-level panning at all," so tie
            // this to the same flag as enablePanDownToClose below.
            enableContentPanningGesture={!disableDrag}
            enablePanDownToClose={!disableDrag}
            // Default ('interactive') expands the sheet's own frame — beyond
            // its dynamically-measured content height — to compensate for the
            // keyboard. That's fine on its own, but whenever the ScrollView is
            // also handling keyboard avoidance (forceMaxContentHeight or
            // keyboardScrollFallback, both wired to
            // automaticallyAdjustKeyboardInsets below), 'interactive' fights
            // that: it grows the sheet's frame independently of what the
            // ScrollView's content actually needs, and that growth can
            // overshoot the sheet's own natural bounds, pushing the top of
            // the sheet (title, Delete) off-screen with mismatched content
            // leaving a gap elsewhere. 'extend' just holds the sheet at its
            // highest detent — no extra keyboard-height-driven growth — so
            // the frame stays stable and the ScrollView's own inset handling
            // does all the keyboard-avoidance work instead. Left as the
            // default for sheets using neither, which still need
            // 'interactive' to grow and clear the keyboard at all.
            keyboardBehavior={
                forceMaxContentHeight || keyboardScrollFallback
                    ? 'extend'
                    : undefined
            }
            // Default ('none') leaves the sheet sized/positioned for the keyboard
            // even after it hides via blur (e.g. tapping outside the input or
            // pressing "Done") rather than the sheet itself closing — 'restore'
            // resizes/repositions it back down. Safe for forceMaxContentHeight
            // sheets too now that keyboardBehavior 'extend' (above) stops them
            // moving at all when the keyboard shows — 'restore' has nothing to
            // undo there, so this only meaningfully applies to the
            // dynamically-sized (non-max-height) sheets that actually grew.
            keyboardBlurBehavior="restore"
            backdropComponent={renderBackdrop}
            backgroundStyle={{
                borderRadius: ROUNDED_CORNER_RADIUS,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
            }}
            animationConfigs={{
                ...animationConfigs,
                // this handles phones with the reduced motion accessibility setting turned on
                // https://github.com/gorhom/react-native-bottom-sheet/issues/1674#issuecomment-1959923945
                ...(reducedMotion && { reduceMotion: ReduceMotion.Never }),
            }}
            handleComponent={() =>
                disableDrag ? undefined : (
                    // Intended to match Sheet's handle component
                    <YStack
                        position="absolute"
                        top={8}
                        left="50%"
                        transform={[{ translateX: -15 }]}
                        backgroundColor="$gray1000"
                        opacity={0.5}
                        borderRadius={30}
                        width={30}
                        height={5}
                        marginHorizontal="auto"
                    />
                )
            }
            containerComponent={
                Platform.OS === 'ios' && isInsideModal
                    ? containerComponent
                    : undefined
            }
            {...props}
        >
            <BottomSheetView
                style={{
                    borderRadius: ROUNDED_CORNER_RADIUS,
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                    overflow: 'hidden',
                }}
            >
                {buildSheetContent(contentHeight)}
            </BottomSheetView>
        </BottomSheetModal>
    );
};
